import { useEffect, useState } from "react";
import baseURL from "../../config/api";
import Pagination from "../../components/Pagination";
import useGroup from "../../hook/useGroup";
import useAuth from "../../hook/useAuth";
import { formatDateTimeForDisplay } from "../../utils/getUserDateFormat";
import Cookies from "js-cookie";

const ViewNotifications = () => {
  const token = Cookies.get("token-my-cdir");
  const { selectedGroupId }: any = useGroup();
  const { auth }: any = useAuth();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    size: 10,
  });

  const [showEntries, setShowEntries] = useState<number>(10);
  const [totalLength, setTotalLength] = useState<number>(0);
  const [selectedNotification, setSelectedNotification] = useState<any>(null);

  useEffect(() => {
    apiCall(paginationModel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, selectedGroupId]);

  const apiCall = (newPaginationModel: { page: number; size: number }) => {
    if (selectedGroupId <= 0) return;

    setLoading(true);

    fetch(
      `${baseURL}notifications/GetNotificationPagingByUserId/${auth?.id},${selectedGroupId},${newPaginationModel.page},${newPaginationModel.size}`, {
      headers: {
        Authorization: `Bearer ${token?.toString()}`
      }
    }
    )
      .then((res) => res.json())
      .then((data) => {
        const result = (data.notifications || []).map((item: any) => ({
          ...item,
          createdAt: item.createdAt
            ? formatDateTimeForDisplay(item.createdAt)
            : "No date",
        }));

        setNotifications(result);
        setTotalLength(data.count || 0);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setNotifications([]);
        setTotalLength(0);
      });
  };

  useEffect(() => {
    if (selectedNotification) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [selectedNotification]);

  return (
    <div className="p-3 xm:p-4 md:p-6 lg:p-10 min-h-screen">
      <div className="flex flex-col xm:flex-row xm:items-center xm:justify-between mb-6 md:mb-8 gap-3">
        <h1 className="text-2xl xm:text-3xl font-bold text-gray-800 break-words">
          Notifications
        </h1>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-[300px] xm:h-[400px]">
          <div className="animate-spin h-10 w-10 border-4 border-secondary border-r-transparent rounded-full" />
        </div>
      ) : (
        <>
          {notifications.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 xm:gap-6">
              {notifications.slice(0, showEntries).map((item) => {
                let parsedPayload: any = {};

                try {
                  parsedPayload = item.payload
                    ? JSON.parse(item.payload)
                    : {};
                } catch { }

                const borderColor =
                  parsedPayload?.type === "announcement"
                    ? "border-secondary"
                    : parsedPayload?.type === "alert"
                      ? "border-red-500"
                      : "border-gray-200";

                const badgeStyle =
                  parsedPayload?.type === "announcement"
                    ? "bg-secondary/10 text-secondary"
                    : parsedPayload?.type === "alert"
                      ? "bg-red-100 text-red-600"
                      : "bg-gray-100 text-gray-600";

                return (
                  <div
                    key={item.id}
                    className={`group rounded-2xl border ${borderColor} bg-white shadow-sm hover:shadow-xl transition-all duration-300 p-4 xm:p-5 flex flex-col justify-between overflow-hidden`}
                  >
                    <div className="flex flex-col xm:flex-row xm:justify-between xm:items-start gap-3">
                      <div className="flex gap-3 items-start min-w-0 flex-1">
                        <div className="w-10 h-10 xm:w-11 xm:h-11 shrink-0 flex items-center justify-center rounded-xl bg-secondary text-white">
                          🔔
                        </div>

                        <div className="min-w-0 flex-1">
                          <h2 className="font-semibold text-gray-800 text-base xm:text-lg break-words">
                            {item.title || "Notification"}
                          </h2>

                          <p className="text-sm text-gray-600 mt-1 line-clamp-2 break-words">
                            {item.body}
                          </p>
                        </div>
                      </div>

                      <span className="text-xs text-gray-400 whitespace-nowrap shrink-0 self-start">
                        {item.createdAt}
                      </span>
                    </div>

                    {parsedPayload?.type && (
                      <span
                        className={`mt-3 w-fit text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full ${badgeStyle}`}
                      >
                        {parsedPayload.type}
                      </span>
                    )}

                    <button
                      onClick={() => setSelectedNotification(item)}
                      className="mt-4 text-sm font-medium text-secondary hover:underline self-start break-words"
                    >
                      View Details →
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center mt-16 xm:mt-20 text-gray-500 text-base xm:text-lg px-4">
              No Notifications Found
            </div>
          )}

          {selectedNotification && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 xm:p-4">
              <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl overflow-hidden animate-fadeIn max-h-[95vh] flex flex-col">
                <div className="flex justify-between items-start gap-3 p-4 xm:p-5 border-b">
                  <h2 className="text-lg xm:text-xl font-semibold text-gray-800 break-words flex-1">
                    {selectedNotification.title || "Notification"}
                  </h2>

                  <button
                    onClick={() => setSelectedNotification(null)}
                    className="text-gray-400 hover:text-gray-600 text-xl shrink-0"
                  >
                    ✕
                  </button>
                </div>

                <div className="p-4 xm:p-5 overflow-y-auto text-gray-700 space-y-4 flex-1">
                  <div className="text-sm text-gray-500 break-words">
                    {selectedNotification.createdAt}
                  </div>

                  <div className="whitespace-pre-line break-words text-sm xm:text-base">
                    {selectedNotification.body}
                  </div>

                  {(() => {
                    let payload: any = {};

                    try {
                      payload = selectedNotification.payload
                        ? JSON.parse(selectedNotification.payload)
                        : {};
                    } catch { }

                    return payload?.extraMessage ? (
                      <div className="bg-gray-100 border rounded-lg p-3 break-words text-sm xm:text-base">
                        {payload.extraMessage}
                      </div>
                    ) : null;
                  })()}
                </div>
              </div>
            </div>
          )}

          {totalLength > 0 && (
            <div className="mt-10 xm:mt-14 border-t pt-4 xm:pt-6">
              <Pagination
                pageOption={[5, 10, 15, 20, 25, 50, 75, 100]}
                totalLength={totalLength}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                showEntries={showEntries}
                setShowEntries={setShowEntries}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ViewNotifications;